import { useEffect, useState } from "react";
import { ImageOff, WifiOff } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { API_BASE } from "../../lib/api";

/**
 * ArticlePhotoPreview -- zeigt nach der Auswahl das Artikelfoto zum
 * Abgleich (#182): ähnliche Namen, richtiger Artikel?
 *
 * Lädt das erste Foto mit Zweck `article_image` am Artikel. Ohne Netz
 * (Flows laufen mit Queue offline) erscheint ein Platzhalter statt eines
 * Fehlers; ohne Foto ein Hinweis, damit der Zustand nicht als Ladefehler
 * gelesen wird. Das Bild wird eingepasst, nicht beschnitten.
 *
 * Props: articleId, articleName, testId
 */
export function ArticlePhotoPreview({ articleId, articleName, testId = "article-photo" }) {
    const [state, setState] = useState({ status: "idle", photo: null });

    useEffect(() => {
        if (!articleId) {
            setState({ status: "idle", photo: null });
            return undefined;
        }
        let cancelled = false;
        setState({ status: "loading", photo: null });
        const params = new URLSearchParams({
            entity_type: "article",
            entity_id: articleId,
            purpose: "article_image",
            limit: "1",
        });
        rpc.get(`/photos?${params.toString()}`).then((res) => {
            if (cancelled) return;
            if (!res.ok) {
                setState({ status: res.status ? "error" : "offline", photo: null });
                return;
            }
            const photo = (res.data || [])[0] || null;
            setState({ status: photo ? "ready" : "empty", photo });
        });
        return () => {
            cancelled = true;
        };
    }, [articleId]);

    if (!articleId) return null;

    const frame =
        "flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-card/40";

    if (state.status === "ready") {
        return (
            <div className="flex items-center gap-3" data-testid={testId} data-state="ready">
                <div className={frame}>
                    <img
                        src={`${API_BASE}/photos/${state.photo.id}/thumb`}
                        alt={articleName ? `Artikelfoto: ${articleName}` : "Artikelfoto"}
                        className="h-full w-full object-contain"
                        data-testid={`${testId}-img`}
                    />
                </div>
                <p className="text-xs text-muted-foreground">
                    Artikelfoto zum Abgleich. Passt das Bild nicht, Position erneut wählen.
                </p>
            </div>
        );
    }

    const messages = {
        loading: "Artikelfoto wird geladen …",
        empty: "Zu diesem Artikel ist kein Foto hinterlegt.",
        offline: "Artikelfoto offline nicht verfügbar.",
        error: "Artikelfoto konnte nicht geladen werden.",
    };
    const Icon = state.status === "offline" ? WifiOff : ImageOff;

    return (
        <div className="flex items-center gap-3" data-testid={testId} data-state={state.status}>
            <div className={frame} aria-hidden="true">
                {state.status === "loading" ? null : <Icon className="h-6 w-6 opacity-50" />}
            </div>
            <p className="text-xs text-muted-foreground" role="status">
                {messages[state.status] || ""}
            </p>
        </div>
    );
}
